<template>
  <!-- -->
  <div class="logic-flow-app">
    <div class="logic-flow-container" :id="divId"></div>
    <el-drawer v-model="showDetail" direction="rtl" size="40%" :title="detailTitle" :before-close="atClose">
      <list-page :show-border="false" :show-shadow="false">
        <list-table :show-border="false" :show-shadow="false">
          <!-- 节点：按 nodeType 分发到对应的配置面板 -->
          <div v-if="currentData.type === 'node'">
            <logic-flow-node-check ref="condition" v-if="currentData.data.nodeType === 'approve'" :data="currentData.data"/>
            <logic-flow-node-duplicate ref="condition" v-else-if="currentData.data.nodeType === 'copy'" :data="currentData.data"/>
            <logic-flow-node-business ref="condition" v-else-if="currentData.data.nodeType === 'business'" :data="currentData.data" :business-nodes="businessNodes"/>
          </div>
          <!-- 连接线 -->
          <logic-flow-edge ref="condition" v-else-if="currentData.type === 'edge'" :multi="currentData.multi" :parent-node="currentData.parentNode" :relation-data="currentData" :column-list="columnList"/>
        </list-table>
        <form-page :show-page="false" :show-border="false" :show-shadow="false">
          <el-button @click="showDetail = false">取消</el-button>
          <el-button @click="apply" type="primary">确认</el-button>
        </form-page>
      </list-page>
    </el-drawer>
  </div>
</template>

<script>
import LogicFlow from "@logicflow/core";
import { DndPanel, SelectionSelect } from '@logicflow/extension';
import {ElMessageBox} from "element-plus";
// logic-flow自定义的组件
import CustomerNode from './atom/CustomerNode.js';
import CustomerPolyline from "./atom/CustomerPolyline";
import CustomerDiamondNode from "./atom/CustomerDiamondNode";

import "@logicflow/core/dist/index.css";
import '@logicflow/extension/lib/style/index.css';
import LogicFlowEdge from "./atom/LogicFlowEdge";
import LogicFlowNodeBusiness from "./atom/LogicFlowNodeBusiness";
import LogicFlowNodeCheck from "./atom/LogicFlowNodeCheck";
import LogicFlowNodeDuplicate from "./atom/LogicFlowNodeDuplicate";
import LogicFlowNodeMessage from "./atom/LogicFlowNodeMessage";
import ListTable from "@/components/h5/layout/ListTable.vue";
import processSettingApi from "@/api/normal/sys/processSettingApi";
import FormPage from "@/components/h5/layout/FormPage.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import customerComponentCheck from "./atom/CustomerComponentCheck";

LogicFlow.use(DndPanel);
LogicFlow.use(SelectionSelect);
export default {
  props: {
    /** 流程图定义（JSON字符串），格式为 LogicFlow 的 {nodes:[], edges:[]} */
    defJson: {default: ""},
    /** 业务类型，用于加载该业务可用的条件字段与业务节点 */
    businessType: {default: ""},
    /** 只读：未选中流程时为 true，禁止拖拽、编辑与双击配置 */
    readonly: {default: false},
    /**
     * 当前编辑的流程 id。
     * 画布只在它变化时重建——用它区分「切换到另一条流程」与「同一条流程的内容更新」，
     * 后者不应重建（重建会清空用户正在编辑的内容）。
     */
    flowId: {default: ""},
  },
  name: "LogicFlow",
  components: {
    FormPage,
    LogicFlowEdge, LogicFlowNodeBusiness, LogicFlowNodeDuplicate, LogicFlowNodeCheck, LogicFlowNodeMessage, ListTable, ListPage
  },
  data() {
    return {
      divId: "",
      logicFlowDomain: {},
      showDetail: false,
      detailTitle: "",
      // 当前正在编辑的数据
      currentData: {},
      flowSettingDetail: {
        flowId: "",
        relations: [],
        nodes: []
      },
      columnList: [], // 可用的字段
      businessNodes: [], // 可用的业务节点
      emitChangePending: false, // 变更通知的微任务去重标记
    };
  },
  created() {
    this.divId = "logic-flow";
  },
  mounted() {
    this.initLogicFlow();
    // 先等业务配置加载完再渲染：工具栏要根据「是否配了业务节点」决定项，
    // 渲染时也要用 columnList / businessNodes 做校验
    Promise.resolve(this.loadBusinessFields()).then(() => {
      this.init();
    });
  },
  methods: {
    initLogicFlow() {
      this.logicFlowDomain= new LogicFlow({
        container: document.querySelector("#" + this.divId),
        // stopMoveGraph: true, // 禁止移动
        stopScrollGraph: true, // 禁止鼠标滚动
        stopZoomGraph: true, // 禁止缩放
        plugins: [DndPanel, SelectionSelect], // 左边的创建插件
        keyboard: {enabled: true}, // 启用内置快捷键
        style: {
          circle: {stroke: "#ccc"},
        },
        background: {
          backgroundColor: "#fafafa"
        },
      });
      this.logicFlowDomain.register(CustomerNode);
      this.logicFlowDomain.register(CustomerPolyline);
      this.logicFlowDomain.register(CustomerDiamondNode);

      // 点击事件
      this.logicFlowDomain.on("node:dbclick", (data) => this.nodeClick(data));
      this.logicFlowDomain.on("edge:dbclick", (data) => this.edgeClick(data));
      /**
       * 图有任何变更就同步给父组件（增删节点/连线、移动位置等）。
       * 之前只在抽屉点「确认」(apply) 时通知，导致「删掉旧连线、拖一条新连线后保存」
       * 这类直接操作画布的行为不会被记录，保存上去的还是加载时的旧数据。
       */
      this.logicFlowDomain.on("graph:updated", () => this.emitGraphChange());
      this.applyReadonly();
    },
    /**
     * 把当前图数据同步给父组件。
     * graph:updated 在拖拽过程中会高频触发，这里做一次微任务级去重，
     * 避免每帧都序列化整个图。
     */
    emitGraphChange() {
      if (this.emitChangePending) {
        return;
      }
      this.emitChangePending = true;
      this.$nextTick().then(() => {
        this.emitChangePending = false;
        // 只读时画布本不该被改动，忽略以防异常回写
        if (this.readonly) {
          return;
        }
        this.$emit("change", this.getData());
      });
    },
    /**
     * 应用只读状态。
     * isSilentMode 会禁用拖拽、连线、节点操作等所有编辑行为，但保留图的正常渲染，
     * 因此未选中流程时用它把画布锁住，而不是简单地不渲染（那样用户看不到任何东西）。
     * 注意：isSilentMode 只在构造时接收，运行中切换需通过 editConfigModel.updateEditConfig。
     */
    applyReadonly() {
      if (!this.logicFlowDomain || !this.logicFlowDomain.graphModel) {
        return;
      }
      const editConfig = this.logicFlowDomain.graphModel.editConfigModel;
      editConfig.updateEditConfig({isSilentMode: this.readonly});
      // 拖拽面板隐藏，避免看起来能拖却拖不动
      this.setDndPanelVisible(!this.readonly);
    },
    setDndPanelVisible(visible) {
      const panel = this.logicFlowDomain.extension && this.logicFlowDomain.extension.dndPanel;
      if (!panel) {
        return;
      }
      const el = panel.domContainer;
      if (el) {
        el.style.display = visible ? "" : "none";
      }
    },
    /**
     * 刷新工具栏。
     * 必须等业务配置加载完再调用：业务节点的可用性取决于该业务是否配置了
     * field_type=900 的记录，没配就不应该出现在工具栏里供人拖拽。
     */
    refreshPanel() {
      this.logicFlowDomain.extension.dndPanel.setPatternItems(this.getPanel());
    },
    /**
     * 加载该业务下的配置项：
     *   field_type 100~500 的作为条件字段（供连线条件编辑器使用）
     *   field_type = 900 的作为业务节点（供业务节点面板选择）
     * 业务配置变更后需重新进入页面才生效，这里不做实时刷新。
     */
    loadBusinessFields() {
      if (!this.businessType) {
        // 没有业务类型时也要刷新工具栏：此时 businessNodes 为空，
        // 业务节点不会出现在工具栏里
        this.refreshPanel();
        return;
      }
      return processSettingApi.queryFieldList(this.businessType).then(list => {
        const all = list || [];
        // fieldType=4（布尔）在条件编辑器里不参与比较，与原逻辑保持一致予以排除
        this.columnList = all.filter(f => f.fieldType !== 4 && f.fieldType !== 900);
        this.businessNodes = all.filter(f => f.fieldType === 900);
        // 业务节点可能有/没有，工具栏要跟着变
        this.refreshPanel();
        // 已渲染的图里若有业务节点，需要重新校验其有效性
        this.revalidateGraph();
      });
    },
    /** 配置加载后重新校验画布上的节点（业务节点是否仍可选等） */
    revalidateGraph() {
      if (!this.defJson) {
        return;
      }
      this.init();
    },
    // 初始化页面, 渲染 defJson 里保存的流程图
    init() {
      // 清空画布，避免切换流程时残留上一条的数据
      this.logicFlowDomain.render({nodes: [], edges: []});
      if (!this.defJson) {
        return;
      }
      let graph;
      try {
        graph = JSON.parse(this.defJson);
      } catch (e) {
        this.$message.error("流程定义内容不是合法的JSON，无法渲染");
        return;
      }
      if (!graph || !graph.nodes) {
        return;
      }
      /**
       * 校验函数（CustomerComponentCheck）期望的数据结构是 {nodes, relations}，
       * 其中 relations 用 parentId/nodeId 表达两端。
       * 而 defJson 存的是 LogicFlow 格式（edges + sourceNodeId/targetNodeId），
       * 字段名对不上，直接传进去会因为 relations 为 undefined 而报错，
       * 所以这里统一做一次转换，节点校验与连线校验共用。
       */
      const relations = (graph.edges || []).map(edge => ({
        id: edge.id,
        parentId: edge.sourceNodeId,
        nodeId: edge.targetNodeId,
        ...(edge.properties || {})
      }));
      const checkData = {nodes: graph.nodes, relations: relations};
      // 补齐工具栏提供的图标与默认文案，老数据可能缺这些属性
      let panels = this.getPanel();
      let nodes = graph.nodes.map(node => {
        let properties = node.properties || {};
        let nNode = {
          id: node.id,
          x: node.x,
          y: node.y,
          type: node.type,
          text: node.text,
          // id 一并放进 properties：校验函数（nodeHasRelation 等）读的是
          // node.properties.id，而图数据里 id 挂在节点外层，不放进去会取到 undefined，
          // 导致所有节点都被误判为「没有来源/去处」而标红
          properties: {...properties, id: node.id},
        };
        // 用工具栏配置补全图标（节点属性里可能没有）
        for (let i = 0; i < panels.length; i++) {
          if (properties.nodeType === panels[i].properties.nodeType) {
            if (!nNode.type) {
              nNode.type = panels[i].type;
            }
            if (!properties.nodeIcon) {
              nNode.properties.nodeIcon = panels[i].properties.nodeIcon;
            }
            if (nNode.type !== "CustomerNode" && !nNode.text) {
              nNode.text = properties.nodeName || panels[i].text;
            }
            break;
          }
        }
        // 校验节点配置是否完整，不完整的在画布上标红。
        // 传 checkData 而非原始 graph —— 校验函数读的是 relations，
        // 而 defJson 里存的是 edges，字段名不一致会直接报错。
        if (properties.nodeType === "approve") {
          customerComponentCheck.checkNodeValid(nNode, checkData);
        } else if (properties.nodeType === "condition") {
          customerComponentCheck.diamondNodeValid(nNode, checkData);
        }
        return nNode;
      });
      let edges = (graph.edges || []).map(edge => ({
        id: edge.id,
        sourceNodeId: edge.sourceNodeId,
        targetNodeId: edge.targetNodeId,
        type: edge.type || "CustomerPolyline",
        text: edge.text,
        properties: edge.properties || {},
        startPoint: edge.startPoint || null,
        endPoint: edge.endPoint || null,
        pointsList: edge.pointsList || null,
      }));
      let nodeInfo = {nodes, edges};
      this.logicFlowDomain.render(nodeInfo);
      // 渲染后校验连线条件（复用上面组装好的 checkData）
      this.validateEdges(checkData);
    },
    /**
     * 校验所有连线的条件配置，不完整的在画布上标红。
     * checkData 为 {nodes, relations} 结构（relationValid 要求的格式）。
     */
    validateEdges(checkData) {
      if (!checkData || !checkData.relations) {
        return;
      }
      checkData.relations.forEach(relation => {
        customerComponentCheck.relationValid({properties: relation}, checkData);
      });
    },
    getInfo() {
      return this.logicFlowDomain.getGraphData();
    },
    /**
     * 导出流程图数据。
     * 直接使用 LogicFlow 的图数据作为 defJson：节点坐标、连线点位本就是画布自己维护的，
     * 之前那套「与页面缓存 merge」的逻辑是为旧接口服务的，现在 defJson 就是唯一数据源。
     */
    getData() {
      return this.getInfo();
    },
    // 点击某个节点
    nodeClick(node) {
      // 只读时不打开配置抽屉
      if (this.readonly) {
        return;
      }
      if (node.data.properties.nodeType === "start" || node.data.properties.nodeType === "end" || node.data.properties.nodeType === "condition") {
        return;
      }
      this.currentData = {
        id: node.data.id,
        type: "node", // 是节点
        data: node.data.properties
      };
      this.showDetail = true;
      this.detailTitle = "节点设置";
    },
    // 点击连接线
    edgeClick(edge) {
      // 只读时不打开条件配置抽屉
      if (this.readonly) {
        return;
      }
      let data = {
        id: edge.data.id,
        type: "edge", // 是连接线
        data: edge.data.properties,
        sourceNodeId: edge.sourceNodeId
      };
      // 通过 sourceNodeId 找到上级节点。
      // 注意 getNodeDataById 可能返回 undefined（节点已被删除等），
      // 直接取 properties 会抛异常导致整个抽屉打不开，所以先判空。
      const parentNode = this.logicFlowDomain.getNodeDataById(edge.data.sourceNodeId);
      if (!parentNode || !parentNode.properties) {
        this.$message.warning("未找到该连线对应的上级节点");
        return;
      }
      const parentType = parentNode.properties.nodeType;
      const edgList = this.logicFlowDomain.getNodeOutgoingEdge(edge.data.sourceNodeId);
      /**
       * multi 决定是否显示「条件分支」配置区。
       * 两种情况需要：
       *   1. 上级有多个出线（真正的多分支）
       *   2. 上级是条件判断节点——它的每条出线本质上就是一个分支，
       *      即便当前只连了一条，也要能配置判断条件
       */
      data.multi = edgList.length > 1 || parentType === "condition";
      // 上级是审批节点时，这条线代表「审批通过」还是「审批拒绝」
      data.checkTypeEnable = parentType === "approve";
      data.parentNode = parentNode.properties;
      this.currentData = data;
      this.showDetail = true;
      this.detailTitle = "审批条件设置";
    },
    // 获取工具栏
    // 节点类型对齐工作流设计：start 发起 / approve 审批 / condition 条件 / copy 抄送 / end 结束
    getPanel() {
      let arr = [];
      // 开始节点
      arr.push({type: 'circle', text: '开始', label: '开始', properties: {nodeType: "start"}, className: "lf-node-start"});
      // 审批节点
      arr.push({label: '审批节点', type: "CustomerNode", properties: {nodeType: "approve", nodeName: "审批", nodeIcon: "lf-node-check"}, className: "lf-node-check"});
      // 业务节点：处理人完成业务操作，引擎回调业务接口落库。
      // 仅当该业务在「业务设置」里配置过业务节点(field_type=900)时才提供，
      // 否则拖出来也没有可选项，徒增困惑。
      if (this.businessNodes.length > 0) {
        arr.push({label: '业务节点', type: "CustomerNode", properties: {nodeType: "business", nodeName: "执行业务", nodeIcon: "lf-node-business"}, className: "lf-node-business"});
      }
      // 条件分支：按顺序求值，首个命中即生效，末尾必须有 default 兜底
      arr.push({type: 'CustomerDiamondNode', text: '条件判断', label: '条件分支', properties: {nodeType: "condition"}, className: "lf-node-diamond"});
      // 抄送节点
      arr.push({label: '抄送节点', type: "CustomerNode", properties: {nodeType: "copy", nodeName: "抄送", nodeIcon: "lf-node-duplicate"}, className: "lf-node-duplicate"});
      // 结束节点
      arr.push({type: 'circle', text: '结束', label: '结束', properties: {nodeType: "end"}, className: "lf-node-end"})
      return arr;
    },
    // 抽屉里点确认：把配置面板的数据写回画布节点/连线，然后重新渲染
    apply() {
      return new Promise((resolve) => {
        // 获取数据，获取数据前每个组件需要单独进行数据验证
        let data = this.$refs.condition.getData();
        // 获取数据
        let logicData = this.getInfo();
        // 找到对应的数据
        if (this.currentData.type === 'node') {
          // 是节点
          for (let i = 0; i < logicData.nodes.length; i++) {
            if (logicData.nodes[i].id === this.currentData.id) {
              logicData.nodes[i].properties = data;
              break;
            }
          }
          this.logicFlowDomain.render(logicData);
        } else if (this.currentData.type === 'edge') {
          // 是线
          for (let i = 0; i < logicData.edges.length; i++) {
            if (logicData.edges[i].id === this.currentData.id) {
              logicData.edges[i].properties = data;
              if (data.relationName) {
                logicData.edges[i].text = data.relationName;
              }
              break;
            }
          }
          // 重新渲染
          this.logicFlowDomain.render(logicData);
        }
        this.showDetail = false;
        // 图数据变了，通知父组件同步 defJson（由父组件决定何时落库）。
        // 与 graph:updated 走同一条路径，避免两处逻辑不一致。
        this.emitGraphChange();
        resolve();
      })
    },
    atClose(done) {
      // 项目只手动注册了 $message，未注册 $confirm，须显式使用 ElMessageBox
      ElMessageBox.confirm("是否保存并退出", "提示", {
        confirmButtonText: "保存并退出",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.apply().then(() => {
          done();
        })
      }).catch(() => {
        done();
      });
    }
  },
  watch: {
    /**
     * 仅在「切换到另一条流程」时重新渲染画布。
     *
     * 不能监听 defJson 的值变化：画布自身编辑（apply）和保存回写（saveGraph）
     * 都会更新 defJson，若据此重新 init()，init() 会先清空画布再重建，
     * 等于把用户刚做的编辑抹掉。
     *
     * 因此改为监听「流程标识」——只有它变了才代表真的换了流程。
     * 优先用 flowId（父组件传入的当前流程 id）。
     */
    flowId() {
      this.$nextTick().then(() => this.init());
    },
    // 选中/取消选中流程时切换可编辑状态
    readonly() {
      this.$nextTick().then(() => this.applyReadonly());
    },
  },
};
</script>

<style lang="scss">
@import "./css/LogicNode";
.form-page{
  border-top: 1px solid var(--el-border-color);
}
.logic-flow-app{
  position: relative;
  height: 100%;
}
.logic-flow-container{
  height: 100%;
}
.lf-dnd-shape{
  width: 30px;
  height: 30px;
  margin-top: 10px;
}
.lf-node-check{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/map-o.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-end{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/dot-circle.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-diamond{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/diamond.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-start{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/circle.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-business{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/flag-o.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-duplicate{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/copy.svg") no-repeat;
    background-size: contain;
  }
}
.lf-node-message{
  .lf-dnd-shape{
    background: url("@/assets/image/icon/email.svg") no-repeat;
    background-size: contain;
  }
}

// 自定义节点的样式-只定义图标
.logic-customer-node{
  .node-header{
    .lf-node-check{
      background: url("@/assets/image/icon/map-o.svg") no-repeat;
      background-size: contain;
    }
    .lf-node-duplicate{
      background: url("@/assets/image/icon/copy.svg") no-repeat;
      background-size: contain;
    }
    .lf-node-message{
      background: url("@/assets/image/icon/email.svg") no-repeat;
      background-size: contain;
    }
    .lf-node-business{
      background: url("@/assets/image/icon/flag-o.svg") no-repeat;
      background-size: contain;
    }
  }
  .node-body{
    font-size: 12px;
    color: #999;
    padding: 10px;
    white-space: nowrap;
  }
}

</style>